import { createHttpClient } from '@adapters/HttpClient';
import { CHANNELS_WITH_FILE_IMPORT } from '@modules/Shops/List/models/Shops';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Combobox } from '@shared/components/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';
import { ShopApiAdapter } from '../../adapters/ShopApiAdapter';

const AMAZON_FBM_CHANNEL_ID = CHANNELS_WITH_FILE_IMPORT[0];

const linkFormSchema = z.object({
  shopId: z.string().min(1),
  skuAmazon: z.string().default(''),
  externalProductId: z.string().default(''),
  externalVariantId: z.string().default(''),
});
type LinkFormValues = z.infer<typeof linkFormSchema>;

const defaultValues: LinkFormValues = {
  shopId: '',
  skuAmazon: '',
  externalProductId: '',
  externalVariantId: '',
};

export interface CreateLinkModalRef {
  open: (onConfirm: (data: any) => void) => void;
}

interface CreateLinkModalProps {
  producto: {
    id: number;
    name: string;
    code: string;
    src: string;
  };
  isEdit?: boolean;
  onCreateLink?: (enlace: { tienda: any; producto: any; variante: any }) => void;
  productId?: string | number;
}

export const CreateLinkModal = forwardRef<CreateLinkModalRef, CreateLinkModalProps>(
  ({ producto, isEdit = false, productId }, ref) => {
    const { t } = useTranslation('createEditProductsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const confirmRef = useRef<(data: any) => void>(() => {});
    const { toast } = useToast();

    const { control, handleSubmit, watch, reset, setValue } = useForm<LinkFormValues>({
      resolver: zodResolver(linkFormSchema),
      defaultValues,
      mode: 'onChange',
    });

    const shopId = watch('shopId');
    const externalProductId = watch('externalProductId');
    const externalVariantId = watch('externalVariantId');
    const skuAmazon = watch('skuAmazon');

    const shopsQuery = useQuery({
      queryKey: ['create-link-shops'],
      queryFn: () => new ShopApiAdapter(createHttpClient(apiClientWH)).getAll(),
      staleTime: 5 * 60 * 1000,
    });

    const selectedShop = shopsQuery.data?.find((s: any) => String(s.id) === shopId);
    const isAmazonFbm = selectedShop?.channel_id === AMAZON_FBM_CHANNEL_ID;

    const productsQuery = useQuery({
      queryKey: ['create-link-shop-products', shopId],
      queryFn: () => new ShopApiAdapter(createHttpClient(apiClientWH)).getProductsByShop(shopId),
      enabled: !!shopId && !isAmazonFbm,
      staleTime: 2 * 60 * 1000,
    });

    useEffect(() => {
      if (!productsQuery.isError) return;
      const err = productsQuery.error as any;
      const data = err?.response?.data;
      const apiMessage =
        data?.message ||
        data?.error?.message ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        data?.error_description ||
        err?.message;
      toast({
        title: t('form.products_error_title'),
        description: apiMessage || t('form.products_error_desc'),
        variant: 'destructive',
      });
    }, [productsQuery.isError, productsQuery.error, toast, t]);

    const selectedProduct = productsQuery.data?.find((p) => String(p.id) === externalProductId);
    const variants = selectedProduct?.variants ?? [];
    const hasVariants = variants.length > 0;

    const productOptions = useMemo(
      () =>
        (productsQuery.data ?? []).map((prod) => ({
          value: String(prod.id),
          label: prod.name,
        })),
      [productsQuery.data],
    );

    const variantOptions = useMemo(
      () =>
        variants.map((v) => ({
          value: String(v.id),
          label: v.title,
        })),
      [variants],
    );

    const linkMutation = useMutation({
      mutationFn: async (values: LinkFormValues) => {
        const adapter = new ProductsApiAdapter(createHttpClient(apiClientWH));
        const resolveProductId = async (): Promise<string | number> => {
          if (productId !== undefined) return productId;
          const lastProduct = await adapter.getLastCreatedProduct();
          if (!lastProduct?.id) {
            throw new Error('No se pudo obtener el producto a enlazar');
          }
          return lastProduct.id;
        };
        const effectiveProductId = await resolveProductId();

        if (isAmazonFbm) {
          await adapter.linkAmazonSku({
            productId: effectiveProductId,
            externalVariantId: values.skuAmazon.trim(),
            shopId: values.shopId,
          });
          return { tienda: values.shopId, producto: null, variante: values.skuAmazon.trim() };
        }

        const variantFallback = selectedProduct?.variant_id;
        const variantId = values.externalVariantId || variantFallback;
        await adapter.createLink({
          productId: effectiveProductId,
          externalProductId: values.externalProductId,
          shopId: values.shopId,
          externalVariantId: variantId ?? undefined,
        });
        return { tienda: values.shopId, producto: values.externalProductId, variante: values.externalVariantId };
      },
      onSuccess: (data) => {
        confirmRef.current(data);
        toast({
          title: t('form.link_success_title'),
          description: t('form.link_success_desc'),
          variant: 'success',
        });
        modalRef.current?.closeModal();
      },
      onError: (error: any) => {
        const status = error?.response?.status;
        let errorMsg: string;
        if (status === 409 || (typeof error?.message === 'string' && error.message.includes('409'))) {
          errorMsg = t('form.error_already_linked');
        } else {
          errorMsg =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            t('form.error_desc');
        }
        toast({
          title: t('form.link_error_title'),
          description: errorMsg,
          variant: 'destructive',
        });
      },
    });

    useImperativeHandle(ref, () => ({
      open: (onConfirm) => {
        confirmRef.current = onConfirm;
        reset(defaultValues);
        modalRef.current?.openModal();
      },
    }));

    const onSubmit = handleSubmit((values) => linkMutation.mutate(values));

    const canSubmit =
      !!shopId &&
      !linkMutation.isPending &&
      (isAmazonFbm ? !!skuAmazon.trim() : !!externalProductId && (!hasVariants || !!externalVariantId));

    const showSkuAmazon = !!shopId && isAmazonFbm;
    const showProductSelect = !!shopId && !isAmazonFbm;
    const showVariantSelect = showProductSelect && !!externalProductId && hasVariants;

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modal.crear_enlace_title')}
        type="default"
        width="lg"
        className="w-full h-full sm:w-[579px] sm:h-auto"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-6 mt-1 pb-4">
            {isEdit && (
              <div className="flex items-center gap-4 mb-2">
                <Product
                  id={producto.id}
                  name={producto.name}
                  code={producto.code}
                  src={producto.src}
                  enableLineClamp={false}
                  showTooltip={false}
                />
              </div>
            )}

            <div>
              <label className="block text-sm mb-2 text-text">{t('modal.tienda_label')}</label>
              <Controller
                control={control}
                name="shopId"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      setValue('externalProductId', '');
                      setValue('externalVariantId', '');
                      setValue('skuAmazon', '');
                    }}
                    disabled={shopsQuery.isLoading}
                  >
                    <SelectTrigger data-testid="link-shop-select">
                      <SelectValue placeholder={t('modal.tienda_placeholder')} />
                    </SelectTrigger>
                    <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                      {shopsQuery.data?.map((shop: any) => (
                        <SelectItem key={shop.id} value={String(shop.id)} className="whitespace-normal">
                          <span className="whitespace-normal break-words">{shop.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div hidden={!showSkuAmazon} className="mt-3">
              <label className="block text-sm mb-2 text-text">{t('modal.sku_amazon_label')}</label>
              <Controller
                control={control}
                name="skuAmazon"
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('modal.sku_amazon_placeholder')}
                    data-testid="link-sku-amazon-input"
                  />
                )}
              />
            </div>

            <div hidden={!showProductSelect} className="mt-3">
              <label className="block text-sm mb-2 text-text">{t('modal.producto_label')}</label>
              <Controller
                control={control}
                name="externalProductId"
                render={({ field }) => (
                  <Combobox
                    options={productOptions}
                    value={field.value}
                    setValue={(v) => {
                      field.onChange(v);
                      setValue('externalVariantId', '');
                    }}
                    placeholder={t('modal.producto_placeholder')}
                    disabled={productsQuery.isLoading || productsQuery.isError}
                    dataTestId="link-product-select"
                    translations={{ notFound: t('modal.producto_not_found') }}
                  />
                )}
              />
            </div>

            <div hidden={!showVariantSelect} className="mt-3">
              <label className="block text-sm mb-2 text-text">{t('modal.variante_label')}</label>
              <Controller
                control={control}
                name="externalVariantId"
                render={({ field }) => (
                  <Combobox
                    options={variantOptions}
                    value={field.value}
                    setValue={field.onChange}
                    placeholder={t('modal.variante_placeholder')}
                    dataTestId="link-variant-select"
                    translations={{ notFound: t('modal.variante_not_found') }}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="cancel-link-modal-button"
            >
              {t('modal.cancelar')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={onSubmit}
              disabled={!canSubmit}
              data-testid="confirm-link-modal-button"
            >
              {t('modal.crear')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateLinkModal.displayName = 'CreateLinkModal';
